/*
  Quantity selector + Buy buttons runtime (Plan B, unit B2). Loaded by
  blocks/product_quantity-selector.liquid, blocks/product_buy-buttons.liquid and
  blocks/product_atc-quantity-selector.liquid.

  Multiverse.money.format(cents, format, stripZeros)   Shopify money format strings in the browser.
  Multiverse.cart.collectLines(form)   -> { form, base, lines, custom }: the form's own line
                                          (id / quantity / properties[] / selling_plan) that
                                          `mv:collect-lines` listeners may replace or extend.
  Multiverse.cart.addLines(lines, { button, productForm, checkout })
                                          POST /cart/add (JSON items + bundled sections), renders the
                                          cart drawer / notification or goes to checkout.

  <mv-buy-buttons>       owns the submit when Dawn's product-form cannot: skip cart, the secondary
                         buy-now button, multi-line adds (quantity breaks rows, gifts / upsells).
                         Toggles `disabled` from the state classes of the refreshed label.
  <mv-quantity-classic>  keeps a classic <quantity-input>'s min / max / step in sync with the
                         selected variant's quantity rules on mv:variant-change.
  <mv-quantity-breaks>   offers: tier selection -> mv:quantity-set, per-unit variant rows
                         (row #1 of the selected offer -> mv:variant-select, others -> gallery),
                         price re-computation on mv:variant-change / row changes, upsells & gifts,
                         PDP price mirroring (update_pdp_prices) and `mv:quantity-break-change`
                         { sectionId, blockId, tier, quantity, price, comparePrice, lines }.

  Contract: docs/superpowers/plans/pB-contracts.md
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };
  // Dawn declares PUB_SUB_EVENTS as a top-level `const` (assets/constants.js): a global lexical
  // binding, NOT a window property, so it has to be reached by name, guarded with typeof.
  const dawnEvents = () => {
    try {
      return typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS || null;
    } catch (e) {
      return null;
    }
  };
  const publishDawn = (name, payload) => {
    const events = dawnEvents();
    if (typeof publish === 'function' && events && events[name]) publish(events[name], payload);
  };

  // ---------- money ----------
  const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  const formatMoney = (cents, format, stripZeros = false) => {
    const template = format || '${{amount}}';
    const match = template.match(PLACEHOLDER);
    const key = match ? match[1] : 'amount';
    let decimals = 2;
    let thousands = ',';
    let decimal = '.';
    switch (key) {
      case 'amount_no_decimals':
        decimals = 0;
        break;
      case 'amount_with_comma_separator':
        thousands = '.';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        decimals = 0;
        thousands = '.';
        break;
      case 'amount_with_space_separator':
        thousands = ' ';
        decimal = ',';
        break;
      case 'amount_no_decimals_with_space_separator':
        decimals = 0;
        thousands = ' ';
        break;
      case 'amount_with_apostrophe_separator':
        thousands = "'";
        break;
      case 'amount_with_period_and_space_separator':
        thousands = ' ';
        break;
      default:
        break;
    }
    const value = Math.round(Number(cents) || 0) / 100;
    const parts = Math.abs(value).toFixed(decimals).split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = parts[1];
    let number = whole;
    if (fraction !== undefined && !(stripZeros && /^0+$/.test(fraction))) number = `${whole}${decimal}${fraction}`;
    if (value < 0) number = `-${number}`;
    return match ? template.replace(PLACEHOLDER, number) : number;
  };
  MV.money = MV.money || {};
  MV.money.format = formatMoney;

  // ---------- cart ----------
  MV.cart = MV.cart || {};
  const cartElement = () => document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  const readFormLine = (form) => {
    const data = new FormData(form);
    const line = { id: data.get('id'), quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1) };
    const properties = {};
    let hasProperties = false;
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || value === '') continue;
      properties[match[1]] = value;
      hasProperties = true;
    }
    if (hasProperties) line.properties = properties;
    const plan = data.get('selling_plan');
    if (plan) line.selling_plan = plan;
    return line;
  };

  MV.cart.collectLines = (form) => {
    const base = readFormLine(form);
    const detail = { form, base, lines: [base], custom: false };
    form.dispatchEvent(new CustomEvent('mv:collect-lines', { bubbles: true, detail }));
    return detail;
  };

  MV.cart.addLines = (lines, { button, productForm, checkout } = {}) => {
    const cart = cartElement();
    const spinner = button ? button.querySelector('.loading__spinner') : null;
    const setBusy = (busy) => {
      if (!button) return;
      button.classList.toggle('loading', busy);
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.toggle('hidden', !busy);
    };
    const showError = (message) => {
      if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage(message);
    };
    showError(false);
    setBusy(true);

    const body = {
      items: lines.map((line) => {
        const item = { id: Number(line.id), quantity: line.quantity };
        if (line.properties) item.properties = line.properties;
        if (line.selling_plan) item.selling_plan = line.selling_plan;
        return item;
      }),
    };
    if (cart && !checkout && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);
    const url = (window.routes && window.routes.cart_add_url) || '/cart/add';

    return fetch(url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || (window.cartStrings && window.cartStrings.error) || 'Error';
          showError(message);
          publishDawn('cartError', { source: 'product-form', productVariantId: lines[0] && lines[0].id, errors: message, message });
          return null;
        }
        if (checkout) {
          window.location.href = '/checkout';
          return response;
        }
        const first = Array.isArray(response.items) ? response.items[0] : response;
        publishDawn('cartUpdate', { source: 'product-form', productVariantId: first && first.variant_id, cartData: response });
        if (cart && response.sections && typeof cart.renderContents === 'function') {
          cart.renderContents(Object.assign({}, first, { sections: response.sections }));
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
        return response;
      })
      .catch((error) => {
        console.error('[mv-cart] add failed', error);
        showError((window.cartStrings && window.cartStrings.error) || error.message);
        return null;
      })
      .finally(() => setBusy(false));
  };

  // ---------- <mv-buy-buttons> ----------
  class MvBuyButtons extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.productForm = this.querySelector('product-form');
      this.button = this.querySelector('.mv-atc');
      this.secondary = this.querySelector('[data-mv-buy-now]');
      this.skipCart = this.dataset.skipCart === 'true';
      if (!this.form) return;

      this._onSubmit = (event) => this.onSubmit(event);
      this.form.addEventListener('submit', this._onSubmit, true);
      if (this.secondary) {
        this._onBuyNow = (event) => {
          event.preventDefault();
          this.add({ checkout: true, button: this.secondary });
        };
        this.secondary.addEventListener('click', this._onBuyNow);
      }
      this._onVariantChange = ({ detail }) => {
        if (!detail || detail.sectionId !== this.sectionId) return;
        this.syncState();
      };
      document.addEventListener('mv:variant-change', this._onVariantChange);
      this.syncState();
    }

    disconnectedCallback() {
      if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit, true);
      if (this.secondary && this._onBuyNow) this.secondary.removeEventListener('click', this._onBuyNow);
      if (this._onVariantChange) document.removeEventListener('mv:variant-change', this._onVariantChange);
    }

    get state() {
      const label = this.querySelector('.mv-atc__label');
      if (!label) return 'available';
      if (label.classList.contains('mv-atc__label--sold-out')) return 'sold-out';
      if (label.classList.contains('mv-atc__label--unavailable')) return 'unavailable';
      return 'available';
    }

    syncState() {
      const disabled = this.state !== 'available';
      [this.button, this.secondary].forEach((element) => {
        if (element) element.toggleAttribute('disabled', disabled);
      });
    }

    onSubmit(event) {
      if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;
      const collected = MV.cart.collectLines(this.form);
      if (!collected.custom && !this.skipCart) return; // Dawn's product-form handles the plain add
      event.preventDefault();
      event.stopImmediatePropagation();
      MV.cart.addLines(collected.lines, { button: this.button, productForm: this.productForm, checkout: this.skipCart });
    }

    add({ checkout = false, button = this.button } = {}) {
      if (this.state !== 'available' || (button && button.getAttribute('aria-disabled') === 'true')) return;
      const collected = MV.cart.collectLines(this.form);
      MV.cart.addLines(collected.lines, { button, productForm: this.productForm, checkout });
    }
  }
  define('mv-buy-buttons', MvBuyButtons);

  // ---------- <mv-quantity-classic> ----------
  class MvQuantityClassic extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.inputId = this.dataset.inputId;
      this._onVariantChange = ({ detail }) => {
        if (!detail || detail.sectionId !== this.sectionId || !detail.html) return;
        this.syncRules(detail.html);
      };
      document.addEventListener('mv:variant-change', this._onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('mv:variant-change', this._onVariantChange);
    }

    syncRules(html) {
      const input = this.querySelector('input[name="quantity"]');
      const fresh = html.getElementById(this.inputId);
      if (!input || !fresh) return;
      ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'].forEach((attr) => {
        const value = fresh.getAttribute(attr);
        if (value === null) input.removeAttribute(attr);
        else input.setAttribute(attr, value);
      });
      const min = parseInt(input.min, 10) || 1;
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const current = parseInt(input.value, 10) || min;
      const clamped = Math.min(Math.max(current, min), max);
      if (String(clamped) !== input.value) {
        input.value = clamped;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const quantityInput = this.querySelector('quantity-input');
      if (quantityInput && typeof quantityInput.validateQtyRules === 'function') quantityInput.validateQtyRules();
    }
  }
  define('mv-quantity-classic', MvQuantityClassic);

  // ---------- <mv-quantity-breaks> ----------
  const KEYWORDS = ['quantity', 'price_each', 'compare_price_each', 'price', 'compare_price', 'amount_saved_rounded', 'amount_saved'];
  const escapeAttr = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"'));

  class MvQuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.formId = this.dataset.formId;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.pdpMoneyFormat = this.dataset.pdpMoneyFormat || this.moneyFormat;
      this.updatePrices = this.dataset.updatePrices === 'true';
      this.updatePdp = this.dataset.updatePdpPrices === 'true';
      this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      this.scrollGallery = this.dataset.scrollGallery !== 'false';
      this.showImages = this.dataset.showVariantImages === 'true';
      this.unavailableText = this.dataset.unavailableText || '[value] - Unavailable';
      this.storageKey = `mv-qb:${this.dataset.productHandle || ''}`;
      this.quantityInput = this.querySelector('[data-mv-quantity]');
      this.tiers = Array.from(this.querySelectorAll('.mv-qb__tier'));
      this.baseVariant = null;

      this._onChange = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.matches('.mv-qb__input')) this.onTierChange();
        else if (target.matches('.mv-qb__select')) this.onRowChange(target);
      };
      this._onClick = (event) => {
        const upsell = event.target.closest('.mv-qb__upsell[data-toggle="true"]');
        if (!upsell || !this.contains(upsell)) return;
        const tier = upsell.closest('.mv-qb__tier');
        if (tier !== this.selectedTier) return; // the label click selects the offer first
        event.preventDefault();
        this.toggleUpsell(upsell);
      };
      this._onKeydown = (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        const upsell = event.target.closest && event.target.closest('.mv-qb__upsell[data-toggle="true"]');
        if (!upsell) return;
        event.preventDefault();
        this.toggleUpsell(upsell);
      };
      this._onVariantChange = ({ detail }) => {
        if (!detail || detail.sectionId !== this.sectionId) return;
        this.onVariantChange(detail.variant);
      };
      this._onProductReady = ({ detail }) => {
        if (!detail || detail.sectionId !== this.sectionId) return;
        this.onVariantChange(detail.variant);
      };
      this._onCollectLines = ({ detail }) => {
        // form.id is shadowed by the <input name="id"> of a product form: read the attribute.
        if (!detail || !detail.form || detail.form.getAttribute('id') !== this.formId) return;
        this.contributeLines(detail);
      };
      this._onSubmit = (event) => {
        const form = event.currentTarget;
        if (form.closest('mv-buy-buttons')) return; // the buy buttons block owns the submit
        const collected = MV.cart.collectLines(form);
        if (!collected.custom) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const button = form.querySelector('[type="submit"]');
        MV.cart.addLines(collected.lines, { button, productForm: form.closest('product-form') });
      };

      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('mv:variant-change', this._onVariantChange);
      document.addEventListener('mv:product-ready', this._onProductReady);
      document.addEventListener('mv:collect-lines', this._onCollectLines);
      this.form = document.getElementById(this.formId);
      if (this.form) this.form.addEventListener('submit', this._onSubmit, true);

      this.restoreUpsells();
      const state = MV.product && MV.product.getState ? MV.product.getState(this.sectionId) : null;
      if (state && state.variant) this.onVariantChange(state.variant);
      else {
        // product state not ready yet (or its ready event fired before this element connected): sync the unit
        // pickers + thumbnails to the page's selected colour once it is, so they never start on the wrong colour
        const retry = () => {
          const st = MV.product && MV.product.getState ? MV.product.getState(this.sectionId) : null;
          if (st && st.variant && (!this.baseVariant || this.baseVariant.id !== st.variant.id)) this.onVariantChange(st.variant);
        };
        requestAnimationFrame(retry);
        window.addEventListener('load', retry, { once: true });
      }
      this.publishSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('mv:variant-change', this._onVariantChange);
      document.removeEventListener('mv:product-ready', this._onProductReady);
      document.removeEventListener('mv:collect-lines', this._onCollectLines);
      if (this.form) this.form.removeEventListener('submit', this._onSubmit, true);
    }

    // ---- state helpers ----
    get product() {
      const state = MV.product && MV.product.getState ? MV.product.getState(this.sectionId) : null;
      return state ? state.product : null;
    }

    get variants() {
      const product = this.product;
      return product && Array.isArray(product.variants) ? product.variants : [];
    }

    get selectedTier() {
      return this.tiers.find((tier) => tier.control && tier.control.checked) || null;
    }

    tierRows(tier) {
      return Array.from(tier.querySelectorAll('.mv-qb__row'));
    }

    findVariant(options) {
      return this.variants.find((variant) => options.every((value, index) => variant.options[index] === value)) || null;
    }

    variantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    // ---- variant rows ----
    rowOptions(row) {
      return Array.from(row.querySelectorAll('.mv-qb__select')).map((select) => select.value);
    }

    setRowOptions(row, options) {
      row.querySelectorAll('.mv-qb__select').forEach((select, index) => {
        if (options[index] !== undefined) select.value = options[index];
      });
    }

    // Marks every <option> of the row against the row's own earlier choices and skips hidden values.
    refreshRowAvailability(row) {
      const variants = this.variants;
      if (!variants.length) return;
      const selects = Array.from(row.querySelectorAll('.mv-qb__select'));
      const chosen = [];
      selects.forEach((select, position) => {
        let selectedVisible = false;
        let firstVisible = null;
        Array.from(select.options).forEach((option) => {
          const value = option.value;
          const matching = variants.filter(
            (variant) => chosen.every((v, i) => variant.options[i] === v) && variant.options[position] === value
          );
          const exists = matching.length > 0;
          const available = matching.some((variant) => variant.available);
          option.toggleAttribute('data-missing', !exists);
          option.toggleAttribute('data-unavailable', exists && !available);
          const hidden = !exists || (!available && this.skipUnavailable);
          option.disabled = hidden;
          option.hidden = hidden;
          option.textContent = exists && !available ? this.unavailableText.replace('[value]', value) : value;
          if (!hidden && !firstVisible) firstVisible = option;
          if (!hidden && option.selected) selectedVisible = true;
        });
        if (!selectedVisible && firstVisible) select.value = firstVisible.value;
        chosen.push(select.value);
      });
    }

    syncRowVariant(row) {
      const variant = this.findVariant(this.rowOptions(row));
      row.dataset.variantId = variant ? variant.id : '';
      if (this.showImages) this.setRowImage(row, variant);
      return variant;
    }

    setRowImage(row, variant) {
      const holder = row.querySelector('[data-mv-row-image]');
      if (!holder) return;
      const image = variant && variant.featured_image;
      if (!image || !image.src) {
        holder.innerHTML = '';
        return;
      }
      const src = `${image.src}${image.src.includes('?') ? '&' : '?'}width=120`;
      let img = holder.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.loading = 'lazy';
        holder.appendChild(img);
      }
      if (img.getAttribute('src') !== src) {
        // The Liquid-rendered <img> carries srcset/sizes; browsers display srcset over src, so a src-only swap kept
        // showing the first colour's photo. Drop them so the new variant image is what's shown.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
        img.alt = image.alt || '';
      }
    }

    onRowChange(select) {
      const row = select.closest('.mv-qb__row');
      const tier = select.closest('.mv-qb__tier');
      if (!row || !tier) return;
      row.dataset.touched = 'true';
      this.refreshRowAvailability(row);
      const variant = this.syncRowVariant(row);
      if (variant && tier === this.selectedTier && row.dataset.row === '0') {
        const current = this.baseVariant ? String(this.baseVariant.id) : null;
        if (String(variant.id) !== current) {
          this.dispatchEvent(new CustomEvent('mv:variant-select', { bubbles: true, detail: { variantId: variant.id } }));
        }
      } else if (variant && this.scrollGallery) {
        this.showInGallery(variant);
      }
      this.recompute();
      this.applyPdpPrice();
      this.publishSelection();
    }

    showInGallery(variant) {
      const mediaId = variant.featured_media ? variant.featured_media.id : null;
      if (!mediaId) return;
      const gallery = document.querySelector(`mv-media-gallery[data-section-id="${escapeAttr(this.sectionId)}"]`);
      if (gallery && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(mediaId, { prepend: false, source: 'quantity-breaks' });
    }

    // ---- offer selection ----
    onTierChange() {
      const tier = this.selectedTier;
      if (!tier) return;
      const quantity = parseInt(tier.dataset.quantity, 10) || 1;
      if (this.quantityInput) this.quantityInput.value = quantity;
      this.dispatchEvent(new CustomEvent('mv:quantity-set', { bubbles: true, detail: { quantity } }));
      const firstRow = this.tierRows(tier)[0];
      if (firstRow && firstRow.dataset.variantId && this.baseVariant && firstRow.dataset.variantId !== String(this.baseVariant.id)) {
        this.dispatchEvent(new CustomEvent('mv:variant-select', { bubbles: true, detail: { variantId: firstRow.dataset.variantId } }));
      }
      this.applyPdpPrice();
      this.publishSelection();
    }

    onVariantChange(variant) {
      const unavailable = !variant || variant.available === false;
      this.tiers.forEach((tier) => tier.classList.toggle('is-unavailable', unavailable));
      if (!variant) return;
      this.baseVariant = variant;
      this.tiers.forEach((tier) => {
        this.tierRows(tier).forEach((row) => {
          if (row.dataset.row === '0' || row.dataset.touched !== 'true') {
            this.setRowOptions(row, variant.options || []);
          }
          this.refreshRowAvailability(row);
          this.syncRowVariant(row);
        });
      });
      this.recompute();
      this.applyPdpPrice();
      this.publishSelection();
    }

    // ---- pricing ----
    compareUnit(variant, mode) {
      if (!variant) return 0;
      const price = Number(variant.price) || 0;
      const compare = Number(variant.compare_at_price) || 0;
      return mode === 'compare_price' && compare > price ? compare : price;
    }

    tierTotals(tier) {
      const base = this.baseVariant;
      if (!base) return null;
      const quantity = parseInt(tier.dataset.quantity, 10) || 1;
      const pctLeft = parseFloat(tier.dataset.pctLeft);
      const fixed = parseFloat(tier.dataset.fixed) || 0;
      const mode = tier.dataset.compareMode || 'compare_price';
      const rows = this.tierRows(tier);
      // Percentage discounts are taken per unit and rounded down to the cent, as Shopify does in the cart.
      const pct = Number.isFinite(pctLeft) ? Math.round((1 - pctLeft) * 100000) / 1000 : 0;
      const net = (cents) => cents - Math.floor((cents * pct) / 100 + 1e-9);
      let gross = 0;
      let compare = 0;
      if (this.updatePrices && rows.length) {
        rows.forEach((row) => {
          const variant = this.variantById(row.dataset.variantId) || base;
          gross += net(Number(variant.price) || 0);
          compare += this.compareUnit(variant, mode);
        });
      } else {
        gross = net(Number(base.price) || 0) * quantity;
        compare = this.compareUnit(base, mode) * quantity;
      }
      const price = Math.max(0, Math.round(gross - fixed));
      const saved = compare - price;
      return {
        quantity,
        price,
        compare,
        saved,
        savedRounded: Math.round(saved / 100) * 100,
        each: Math.round(price / quantity),
        compareEach: Math.round(compare / quantity),
      };
    }

    renderText(template, totals) {
      const values = {
        quantity: totals.quantity,
        price: formatMoney(totals.price, this.moneyFormat, true),
        compare_price: formatMoney(totals.compare, this.moneyFormat, true),
        price_each: formatMoney(totals.each, this.moneyFormat, true),
        compare_price_each: formatMoney(totals.compareEach, this.moneyFormat, true),
        amount_saved: formatMoney(totals.saved, this.moneyFormat, true),
        amount_saved_rounded: formatMoney(totals.savedRounded, this.moneyFormat, true),
      };
      return KEYWORDS.reduce((text, keyword) => text.split(`[${keyword}]`).join(values[keyword]), template || '');
    }

    recompute() {
      this.tiers.forEach((tier) => {
        const totals = this.tierTotals(tier);
        if (!totals) return;
        tier.dataset.price = totals.price;
        tier.dataset.comparePrice = totals.compare;
        tier.querySelectorAll('.mv-qb__dynamic').forEach((element) => {
          const html = this.renderText(element.dataset.mvText, totals);
          if (element.innerHTML !== html) element.innerHTML = html;
          if (element.hasAttribute('data-mv-compare')) element.classList.toggle('hidden', totals.compare <= totals.price);
        });
      });
    }

    applyPdpPrice() {
      if (!this.updatePdp) return;
      const tier = this.selectedTier;
      if (!tier || tier.dataset.price === undefined) return;
      const price = Number(tier.dataset.price);
      const compare = Number(tier.dataset.comparePrice);
      const money = formatMoney(price, this.pdpMoneyFormat);
      const moneyCompare = formatMoney(compare, this.pdpMoneyFormat);
      const scope = this.closest('mv-product') || document;
      const priceBlock = document.getElementById(`price-${this.sectionId}`);
      if (priceBlock) {
        priceBlock.querySelectorAll('.price').forEach((root) => {
          const regular = root.querySelector('.price__regular .price-item--regular');
          const sale = root.querySelector('.price__sale .price-item--sale');
          const was = root.querySelector('.price__sale .price__compare .price-item--regular');
          if (regular) regular.textContent = money;
          if (sale) sale.textContent = money;
          if (was) was.textContent = moneyCompare;
          root.classList.toggle('price--on-sale', compare > price);
        });
      }
      scope.querySelectorAll('.mv-atc__price-value').forEach((element) => {
        element.textContent = money;
      });
    }

    // ---- upsells / gifts ----
    upsellKey(upsell) {
      return `${this.storageKey}:${upsell.dataset.handle}`;
    }

    restoreUpsells() {
      this.querySelectorAll('.mv-qb__upsell[data-toggle="true"]').forEach((upsell) => {
        let stored = null;
        try {
          stored = window.localStorage.getItem(this.upsellKey(upsell));
        } catch (e) {
          stored = null;
        }
        if (stored === null) return;
        this.setUpsell(upsell, stored === '1', false);
      });
    }

    setUpsell(upsell, selected, persist = true) {
      upsell.classList.toggle('is-selected', selected);
      upsell.setAttribute('aria-checked', selected ? 'true' : 'false');
      if (!persist) return;
      try {
        window.localStorage.setItem(this.upsellKey(upsell), selected ? '1' : '0');
      } catch (e) {
        /* storage unavailable */
      }
    }

    toggleUpsell(upsell) {
      const selected = !upsell.classList.contains('is-selected');
      // The same product may sit in several offers: keep every copy in step.
      this.querySelectorAll(`.mv-qb__upsell[data-toggle="true"][data-handle="${escapeAttr(upsell.dataset.handle)}"]`).forEach((copy) =>
        this.setUpsell(copy, selected, copy === upsell)
      );
      this.publishSelection();
    }

    // ---- lines ----
    buildLines(base) {
      const tier = this.selectedTier;
      if (!tier) return { lines: base ? [base] : [], custom: false };
      const quantity = parseInt(tier.dataset.quantity, 10) || 1;
      const extras = {};
      if (base && base.properties) extras.properties = base.properties;
      if (base && base.selling_plan) extras.selling_plan = base.selling_plan;
      const rows = this.tierRows(tier);
      let lines = [];
      if (rows.length) {
        const grouped = new Map();
        rows.forEach((row) => {
          const id = row.dataset.variantId || (base && base.id) || (this.baseVariant && this.baseVariant.id);
          if (!id) return;
          grouped.set(String(id), (grouped.get(String(id)) || 0) + 1);
        });
        grouped.forEach((count, id) => lines.push(Object.assign({ id, quantity: count }, extras)));
      } else {
        const id = (base && base.id) || (this.baseVariant && this.baseVariant.id);
        if (id) lines.push(Object.assign({ id, quantity }, extras));
      }
      let custom = false;
      tier.querySelectorAll('.mv-qb__upsell').forEach((upsell) => {
        const isGift = upsell.dataset.type === 'gift';
        if (!isGift && !upsell.classList.contains('is-selected')) return;
        if (!upsell.dataset.variantId) return;
        lines.push({ id: upsell.dataset.variantId, quantity: 1, properties: { _mv_offer: isGift ? 'gift' : 'upsell' } });
        custom = true;
      });
      if (!custom && base) {
        custom = lines.length !== 1 || String(lines[0].id) !== String(base.id) || lines[0].quantity !== base.quantity;
      }
      return { lines, custom };
    }

    contributeLines(detail) {
      const built = this.buildLines(detail.base);
      if (!built.custom) return;
      detail.lines = built.lines;
      detail.custom = true;
    }

    publishSelection() {
      const tier = this.selectedTier;
      if (!tier) return;
      const detail = {
        sectionId: this.sectionId,
        blockId: this.blockId,
        tier: parseInt(tier.dataset.tier, 10),
        quantity: parseInt(tier.dataset.quantity, 10) || 1,
        price: tier.dataset.price !== undefined ? Number(tier.dataset.price) : null,
        comparePrice: tier.dataset.comparePrice !== undefined ? Number(tier.dataset.comparePrice) : null,
        lines: this.buildLines(null).lines,
      };
      this.dispatchEvent(new CustomEvent('mv:quantity-break-change', { bubbles: true, detail }));
    }
  }
  define('mv-quantity-breaks', MvQuantityBreaks);
})();
